import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, SegmentedControl, type SegmentedControlOption } from '@ui';
import { MemberAvatar } from '@entities/group';
import {
  CheckboxTick,
  SplitAmountIcon,
  SplitEvenlyIcon,
  SplitPercentageIcon,
  SplitSharesIcon,
  X,
} from '@ui/icons';
import {
  cn,
  currencySymbol,
  fromMinorUnit,
  fromMinorUnitFormatted,
  toMinorUnit,
} from '@shared/lib';
import { toast } from '@shared/lib/toast';
import { t } from '@shared/i18n';
import { calculateAutoSplit, type SplitTypeWire } from '../model/autoSplit';

export type SplitMode = 'evenly' | 'shares' | 'percentage' | 'amount';

/**
 * Who participates in the split — the EXPLICIT mode the opener passes; the Paid-by selection
 * itself never decides this (who PAID and who OWES are separate concepts):
 *  - `'selectedMembers'` — only the members the seed (`initial`) marks split-enabled are listed
 *    and split (the Paid-by chained flow, via `PaidBySplitModal`), on EVERY split-by tab —
 *    evenly included. The participants stay pinned to those members for the popup's whole life.
 *  - `'evenly'` (default) — every `members` entry is listed from the start (the Split field).
 */
export type SplitParticipantsMode = 'selectedMembers' | 'evenly';

/** `SplitMode` → the backend `split_type` value sent on `split_configs[i][split_type]`. */
export const SPLIT_TYPE_WIRE: Record<SplitMode, SplitTypeWire> = {
  evenly: 'Equal',
  shares: 'Shares',
  percentage: 'Percentage',
  amount: 'Amount',
};

/** Backend `split_type` → `SplitMode` — reopen the modal on the tab the split was last saved with. */
const MODE_FROM_WIRE: Record<string, SplitMode> = {
  Equal: 'evenly',
  Shares: 'shares',
  Percentage: 'percentage',
  Amount: 'amount',
};

export interface SplitMember {
  id: string;
  name: string;
  avatarUrl: string | null;
  phone: string;
  /** Fallback contact shown when the member has no phone on file (e.g. the signed-in user, who
   *  may have only an email). '' when the API reports none. */
  email: string;
  isYou: boolean;
}

/** One participant's resolved split, emitted on Save. `expenseAmountMinor` is minor units. */
export interface SplitResultEntry {
  userId: string;
  expenseAmountMinor: number;
  splitEnabled: boolean;
  splitType: string;
  shares?: number;
  percentage?: number;
  amount?: number;
}

export interface SplitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: SplitMember[];
  totalMinor: number;
  currency: string;
  payerName: string;
  /** The previously-saved split, if any — reopens the modal on that tab with its values. */
  initial?: readonly SplitResultEntry[] | undefined;
  /** Participant scoping — see {@link SplitParticipantsMode}. Defaults to `'evenly'`. */
  splitMode?: SplitParticipantsMode;
  /** Called with every listed member's resolved split when the user taps Save. */
  onSave: (entries: SplitResultEntry[]) => void;
}

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Keep at most 2 decimal places while typing (e.g. "5.444" → "5.44", "12.5" → "12.5"). */
const to2dp = (v: string): string => {
  const m = /^\d*\.?\d{0,2}/.exec(v);
  return m ? m[0] : v;
};

/**
 * Split `remaining` percent across `ids`, each to 2 decimals (e.g. 75% / 2 → "37.50").
 * The entire remainder difference is absorbed on the LAST participant (matching mobile's `absorbOnLast` rule).
 */
function distributePctEven(ids: string[], remaining: number): Record<string, string> {
  const out: Record<string, string> = {};
  const n = ids.length;
  if (n === 0) return out;
  const totalBp = Math.round(remaining * 100); // percent → basis points (hundredths of a %)
  const roundedBp = Math.round(totalBp / n);
  const firstCount = n - 1;
  const lastBp = Math.max(0, totalBp - firstCount * roundedBp);

  ids.forEach((id, i) => {
    const bp = i === n - 1 ? lastBp : roundedBp;
    out[id] = (bp / 100).toFixed(2);
  });
  return out;
}

/**
 * Percentage auto-fill: members the user has hand-typed ("manual") keep their value; the remaining
 * `100 − Σ manual` is spread across the OTHER checked members (2 dp). Until the user types anything,
 * every box stays at its default (0.00). Re-run on each edit and on check/uncheck.
 */
function balancePct(
  memberIds: string[],
  checkedMap: Record<string, boolean>,
  manualMap: Record<string, boolean>,
  rawPct: Record<string, string>,
): Record<string, string> {
  const out = { ...rawPct };
  const checkedIds = memberIds.filter((id) => checkedMap[id]);
  // While the user hasn't typed ANYTHING yet, keep the 0.00 defaults. Once any value exists, the
  // CHECKED members always fill 100% — so unchecking a payer redistributes their share to the rest
  // (e.g. Admin 25 unchecked → the other two → 50 / 50).
  const anyManual = memberIds.some((id) => manualMap[id]);
  if (!anyManual) {
    // Nothing hand-typed but values present ⇒ they were auto-filled by "Select all": keep the
    // checked set summing to 100 by re-spreading evenly (uncheck one of 25×4 → 33.34/33.33/33.33).
    const anyValue = memberIds.some((id) => num(rawPct[id] ?? '0') !== 0);
    if (!anyValue) return out;
    const even = distributePctEven(checkedIds, 100);
    memberIds.forEach((id) => {
      out[id] = even[id] ?? '0.00';
    });
    return out;
  }
  const manualSum = checkedIds
    .filter((id) => manualMap[id])
    .reduce((acc, id) => acc + num(rawPct[id] ?? '0'), 0);
  const autoIds = checkedIds.filter((id) => !manualMap[id]);
  const autoValues = distributePctEven(autoIds, Math.max(0, 100 - manualSum));
  autoIds.forEach((id) => {
    out[id] = autoValues[id] ?? '0.00';
  });
  return out;
}

/**
 * Split `remainingMinor` evenly across `ids` as 2-dp major strings (e.g. 7500 / 2 → "37.50").
 * The entire remainder is absorbed on the LAST participant (matching mobile's `absorbOnLast` rule).
 */
function distributeAmtEven(ids: string[], remainingMinor: number): Record<string, string> {
  const out: Record<string, string> = {};
  const n = ids.length;
  if (n === 0) return out;
  const base = Math.floor(remainingMinor / n);
  const rem = remainingMinor - base * n;
  ids.forEach((id, i) => {
    out[id] = fromMinorUnit(base + (i === n - 1 ? rem : 0));
  });
  return out;
}

/**
 * Amount auto-fill (mirrors `balancePct` but in currency): hand-typed members keep their amount; the
 * remaining `total − Σ manual` is spread across the other checked members (2 dp). Every box stays at
 * its default (0.00) until the user types anything; then the checked amounts always sum to the total.
 */
function balanceAmt(
  memberIds: string[],
  checkedMap: Record<string, boolean>,
  manualMap: Record<string, boolean>,
  rawAmt: Record<string, string>,
  totalMinor: number,
): Record<string, string> {
  const out = { ...rawAmt };
  const toMinor = (v: string): number => {
    try {
      return toMinorUnit(v);
    } catch {
      return 0;
    }
  };
  const checkedIds = memberIds.filter((id) => checkedMap[id]);
  const anyManual = memberIds.some((id) => manualMap[id]);
  if (!anyManual) {
    // Nothing hand-typed but values present ⇒ auto-filled by "Select all": keep the checked set
    // summing to the total by re-spreading evenly (same rule as `balancePct`).
    const anyValue = memberIds.some((id) => toMinor(rawAmt[id] ?? '0') !== 0);
    if (!anyValue) return out;
    const even = distributeAmtEven(checkedIds, totalMinor);
    memberIds.forEach((id) => {
      out[id] = even[id] ?? '0.00';
    });
    return out;
  }
  const manualSum = checkedIds
    .filter((id) => manualMap[id])
    .reduce((acc, id) => acc + toMinor(rawAmt[id] ?? '0'), 0);
  const autoIds = checkedIds.filter((id) => !manualMap[id]);
  const autoValues = distributeAmtEven(autoIds, Math.max(0, totalMinor - manualSum));
  autoIds.forEach((id) => {
    out[id] = autoValues[id] ?? '0.00';
  });
  return out;
}

/**
 * Compute each included member's share in minor units for the active mode, via the shared
 * `calculateAutoSplit` port of the mobile helper — so web and mobile distribute + round identically
 * and the enabled shares always reconcile to `total`. `included` are the checked (enabled) members.
 */
function computeAmounts(
  mode: SplitMode,
  included: string[],
  total: number,
  shares: Record<string, string>,
  pct: Record<string, string>,
  amt: Record<string, string>,
): Record<string, number> {
  const users = included.map((id) => ({
    id,
    splitEnabled: true,
    ...(mode === 'shares' ? { share: num(shares[id] ?? '0') } : {}),
    ...(mode === 'percentage' ? { percentage: num(pct[id] ?? '0') } : {}),
    ...(mode === 'amount' ? { amount: num(amt[id] ?? '0') } : {}),
  }));
  return calculateAutoSplit(total, SPLIT_TYPE_WIRE[mode], users);
}

/** Small purple checkbox matching the Figma. */
function MemberCheck({ checked }: { checked: boolean; label?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-md transition-colors',
        checked ? 'bg-violet text-white' : 'bg-[#E1E1E1]',
      )}
    >
      {checked ? <CheckboxTick className="size-3" /> : null}
    </span>
  );
}

/** The numeric control on the right of a row (varies by mode). */
function RowControl({
  mode,
  currency,
  amount,
  shareValue,
  pctValue,
  amtValue,
  onShare,
  onPct,
  onPctBlur,
  onAmt,
  onAmtBlur,
  ariaName,
}: {
  mode: SplitMode;
  currency: string;
  amount: number;
  shareValue: string;
  pctValue: string;
  amtValue: string;
  onShare: (v: string) => void;
  onPct: (v: string) => void;
  onPctBlur: () => void;
  onAmt: (v: string) => void;
  onAmtBlur: () => void;
  ariaName: string;
}) {
  const box =
    'h-10 w-20 rounded-lg border border-fg-strong bg-surface-raised px-2 text-right text-sm text-fg tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  // Amounts run longer than a percentage ("12345.00") — the 65px box clipped them; give the
  // currency box the room the other editable fields get.
  const amountBox = box.replace('w-20', 'w-24');
  const line =
    'h-10 w-16.25 rounded-none border-b border-fg-strong bg-surface-raised px-3 text-right text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  if (mode === 'evenly') {
    return (
      <span className="shrink-0 text-base font-semibold text-fg">
        {currencySymbol(currency)}
        {fromMinorUnitFormatted(amount)}
      </span>
    );
  }
  if (mode === 'shares') {
    // A share count is a unitless ratio (e.g. "2 shares" vs "1 share" of the pool), not a currency
    // amount — no currency symbol belongs next to it (unlike "amount" mode, which genuinely is one).
    return (
      <span className="flex shrink-0 items-end gap-1.5">
        <input
          type="text"
          inputMode="numeric"
          aria-label={`${ariaName} shares`}
          value={shareValue}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onShare('');
              return;
            }
            if (!/^\d+$/.test(raw)) return;
            const parsed = parseInt(raw, 10);
            if (parsed > 20) return;
            onShare(String(parsed));
          }}
          className={line}
        />
      </span>
    );
  }
  if (mode === 'percentage') {
    return (
      <span className="flex shrink-0 items-center gap-1.5">
        <input
          type="text"
          inputMode="numeric"
          aria-label={`${ariaName} percentage`}
          value={pctValue}
          onFocus={(e) => {
            // Default boxes read "0.00"; select it so the first keystroke REPLACES it (type "2" →
            // "2", not "0.002"→"0.00"). A real, non-zero value keeps normal cursor placement.
            if (num(e.target.value) === 0) e.target.select();
          }}
          onChange={(e) => onPct(e.target.value)}
          onBlur={onPctBlur}
          className={box}
        />
        <span className="text-sm text-fg">%</span>
      </span>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span className="text-sm text-fg">{currencySymbol(currency)}</span>
      <input
        type="text"
        inputMode="decimal"
        aria-label={`${ariaName} amount`}
        value={amtValue}
        // Clicking into the box selects its whole value so the first keystroke REPLACES it
        // (typing over "0.00" / a previous amount), rather than appending to it.
        onFocus={(e) => e.target.select()}
        onClick={(e) => e.currentTarget.select()}
        onChange={(e) => onAmt(e.target.value)}
        onBlur={onAmtBlur}
        className={amountBox}
      />
    </span>
  );
}

/** Form body — remounts each open so defaults reset from the latest members/total. */
function SplitForm({
  members,
  totalMinor,
  currency,
  payerName,
  initial,
  splitMode = 'evenly',
  onClose,
  onSave,
}: {
  members: SplitMember[];
  totalMinor: number;
  currency: string;
  payerName: string;
  initial?: readonly SplitResultEntry[] | undefined;
  splitMode?: SplitParticipantsMode;
  onClose: () => void;
  onSave: (entries: SplitResultEntry[]) => void;
}) {
  const s = t.newBill.splitModal;
  const youMember = members.find((m) => m.isYou);

  const [first, ...rest] = payerName.trim().split(/\s+/);
  const shortPayerName = rest.length ? `${first} ${rest[rest.length - 1]?.[0] ?? ''}` : first ?? '';
  const isPayerYou = shortPayerName === youMember?.name;
  const displayPayerName = isPayerYou ? s.you : shortPayerName;

  // Seed from the previously-saved split (`initial`) so re-opening lands on the SAME tab + values;
  // otherwise the evenly defaults. Each saved entry carries its splitType + shares/percentage/amount.
  const initialById = new Map((initial ?? []).map((e) => [e.userId, e]));
  const initialMode: SplitMode =
    initial && initial.length > 0
      ? MODE_FROM_WIRE[initial[0]?.splitType ?? ''] ?? 'evenly'
      : 'evenly';

  // 'selectedMembers' (the Paid-by chained flow) pins the LIST to the seed's split-enabled
  // members — on EVERY split-by tab, evenly included: the participants stay the Paid-by payers
  // for the whole life of that popup. 'evenly' (the Split field) lists the whole group instead.
  const seedEnabledIds = new Set(
    (initial ?? []).filter((e) => e.splitEnabled).map((e) => e.userId),
  );
  const payerScoped = members.filter((m) => seedEnabledIds.has(m.id));
  const scopedMembers =
    splitMode === 'selectedMembers' && payerScoped.length > 0 ? payerScoped : members;
  const memberIds = scopedMembers.map((m) => m.id);

  const [mode, setMode] = useState<SplitMode>(initialMode);
  // With NO saved split every member starts checked (fresh open, evenly across everyone). With
  // one, a member ABSENT from it was NOT part of that split — default them UNCHECKED so the
  // reopened modal shows exactly the split that was saved (and that the payload carries), instead
  // of silently displaying an everyone-evenly split the saved data doesn't contain.
  const hasInitial = (initial?.length ?? 0) > 0;
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      members.map((m) => [m.id, initialById.get(m.id)?.splitEnabled ?? !hasInitial]),
    ),
  );
  const [shares, setShares] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      members.map((m) => {
        const sh = initialById.get(m.id)?.shares;
        return [m.id, sh != null ? String(sh) : '1'];
      }),
    ),
  );
  // Percentage + amount inputs default to 0.00. The `*Manual` maps track which boxes the user typed;
  // the others auto-fill with the remainder (% of 100, or currency of the total). Saved values seed as
  // manual so they don't get auto-overwritten on reopen.
  const [pctManual, setPctManual] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      members.filter((m) => initialById.get(m.id)?.percentage != null).map((m) => [m.id, true]),
    ),
  );
  const [pct, setPct] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      members.map((m) => {
        const p = initialById.get(m.id)?.percentage;
        return [m.id, p != null ? p.toFixed(2) : '0.00'];
      }),
    ),
  );
  const [amtManual, setAmtManual] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      members.filter((m) => initialById.get(m.id)?.amount != null).map((m) => [m.id, true]),
    ),
  );
  const [amt, setAmt] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      members.map((m) => {
        const a = initialById.get(m.id)?.amount;
        return [m.id, a != null ? a.toFixed(2) : '0.00'];
      }),
    ),
  );

  // Edit a member's % → mark it manual, then auto-fill the remaining % across the OTHER, still
  // untouched members. A hand-typed value is NEVER rewritten: no capping to what's left, and once
  // every checked member is manual nothing is silently re-balanced — Save's add-up guard reports the
  // exact shortfall/excess instead. (This used to demote the LAST other manual member back to auto
  // and cap the typed value, so entering 10 / 10 / 10 on a 90 bill turned one 10 into 70.)
  const handlePctChange = (id: string, rawV: string) => {
    const v = to2dp(rawV);
    const nextManual = { ...pctManual, [id]: true };
    setPctManual(nextManual);
    setPct((prev) => balancePct(memberIds, checked, nextManual, { ...prev, [id]: v }));
  };
  // Edit a member's amount → mark it manual, then auto-fill the remainder across the OTHER, still
  // untouched members. Same rule as `handlePctChange`: a hand-typed amount is never capped or
  // re-balanced away; a mismatch is caught by Save's add-up guard.
  const handleAmtChange = (id: string, rawV: string) => {
    const v = to2dp(rawV);
    const nextManual = { ...amtManual, [id]: true };
    setAmtManual(nextManual);
    setAmt((prev) => balanceAmt(memberIds, checked, nextManual, { ...prev, [id]: v }, totalMinor));
  };
  // On blur, normalise a typed value to 2 decimals (e.g. "23" → "23.00") to match the auto boxes.
  const handlePctBlur = (id: string) => {
    setPct((prev) => {
      const v = prev[id] ?? '';
      return v === '' ? prev : { ...prev, [id]: num(v).toFixed(2) };
    });
  };
  const handleAmtBlur = (id: string) => {
    setAmt((prev) => {
      const v = prev[id] ?? '';
      return v === '' ? prev : { ...prev, [id]: num(v).toFixed(2) };
    });
  };
  // Check/uncheck a member → re-fill the auto members (both % and amount) across the new checked set.
  const toggleChecked = (id: string) => {
    const nextChecked = { ...checked, [id]: !checked[id] };
    setChecked(nextChecked);
    setPct((prev) => {
      const balanced = balancePct(memberIds, nextChecked, pctManual, prev);
      // Split by percentage: when every checked member was typed manually (no auto row to absorb the
      // remainder), unchecking one would leave the rest not adding to 100. Give the leftover % to the
      // LAST checked member so they still total 100 (e.g. uncheck the first ⇒ the other shows 100%).
      if (mode === 'percentage') {
        const checkedIds = memberIds.filter((mid) => nextChecked[mid]);
        const anyAuto = checkedIds.some((mid) => !pctManual[mid]);
        const last = checkedIds.at(-1);
        if (checkedIds.length > 0 && !anyAuto && last) {
          const othersSum = checkedIds
            .filter((mid) => mid !== last)
            .reduce((acc, mid) => acc + num(balanced[mid] ?? '0'), 0);
          return { ...balanced, [last]: Math.max(0, 100 - othersSum).toFixed(2) };
        }
      }
      return balanced;
    });
    setAmt((prev) => {
      const balanced = balanceAmt(memberIds, nextChecked, amtManual, prev, totalMinor);
      // Split by amount: when every checked member was typed manually (no auto row to absorb the
      // remainder), unchecking one would leave the rest not adding up. Give the leftover to the LAST
      // checked member so they still total the bill (e.g. uncheck one of 4+3 ⇒ the other shows 7).
      if (mode === 'amount') {
        const checkedIds = memberIds.filter((mid) => nextChecked[mid]);
        const anyAuto = checkedIds.some((mid) => !amtManual[mid]);
        const last = checkedIds.at(-1);
        if (checkedIds.length > 0 && !anyAuto && last) {
          const toMinor = (v: string): number => {
            try {
              return toMinorUnit(v);
            } catch {
              return 0;
            }
          };
          const othersSum = checkedIds
            .filter((mid) => mid !== last)
            .reduce((acc, mid) => acc + toMinor(balanced[mid] ?? '0'), 0);
          return { ...balanced, [last]: fromMinorUnit(Math.max(0, totalMinor - othersSum)) };
        }
      }
      return balanced;
    });
  };

  const included = scopedMembers.filter((m) => checked[m.id]).map((m) => m.id);
  // Every listed member checked ⇒ the toggle link reads "Clear all"; otherwise "Select all".
  const allChecked = scopedMembers.length > 0 && scopedMembers.every((m) => checked[m.id]);
  const amounts = computeAmounts(mode, included, totalMinor, shares, pct, amt);

  const handleSave = () => {
    // Add-up guards (mobile parity): the ENTERED amounts must total the bill and the ENTERED
    // percentages must total 100 — else a toast says exactly how much to add / remove, and the save is
    // blocked. Evenly / shares always reconcile (auto-computed), so they need no guard. Amounts are
    // 2-decimal, so compare with a 0.01 tolerance.
    // At least one member must be part of the split — block Save (with a toast) when none are checked.
    if (included.length === 0) {
      toast.error(s.validation.noMembers);
      return;
    }
    const sym = currencySymbol(currency);
    if (mode === 'amount') {
      const totalMajor = totalMinor / 100;
      const enteredMajor = included.reduce((acc, id) => acc + num(amt[id] ?? '0'), 0);
      const diff = Math.round((totalMajor - enteredMajor) * 100) / 100;
      if (Math.abs(diff) >= 0.01) {
        const total = `${sym}${totalMajor.toFixed(2)}`;
        const amount = `${sym}${Math.abs(diff).toFixed(2)}`;
        toast.error(
          (diff > 0 ? s.validation.amountAddMore : s.validation.amountRemove)
            .replace('{total}', total)
            .replace('{amount}', amount),
        );
        return;
      }
    } else if (mode === 'percentage') {
      const enteredPct = included.reduce((acc, id) => acc + num(pct[id] ?? '0'), 0);
      const diff = Math.round((100 - enteredPct) * 100) / 100;
      if (Math.abs(diff) >= 0.01) {
        const percent = Math.abs(diff).toFixed(2);
        toast.error(
          (diff < 0 ? s.validation.percentRemove : s.validation.percentAddMore).replace(
            '{percent}',
            percent,
          ),
        );
        return;
      }
    }
    const entries: SplitResultEntry[] = scopedMembers.map((mem) => {
      const enabled = Boolean(checked[mem.id]);
      return {
        userId: mem.id,
        expenseAmountMinor: enabled ? amounts[mem.id] ?? 0 : 0,
        splitEnabled: enabled,
        splitType: SPLIT_TYPE_WIRE[mode],
        ...(mode === 'shares' ? { shares: num(shares[mem.id] ?? '0') } : {}),
        ...(mode === 'percentage' ? { percentage: num(pct[mem.id] ?? '0') } : {}),
        ...(mode === 'amount' ? { amount: num(amt[mem.id] ?? '0') } : {}),
      };
    });
    onSave(entries);
    onClose();
  };

  const splitOptions: SegmentedControlOption[] = [
    {
      label: s.tabs.evenly,
      value: 'evenly',
      icon: <SplitEvenlyIcon className="text-accent h-3.75" />,
    },
    {
      label: s.tabs.amount,
      value: 'amount',
      icon: <SplitAmountIcon className="text-accent" />,
    },
    {
      label: s.tabs.shares,
      value: 'shares',
      icon: <SplitSharesIcon className="text-accent" />,
    },
    {
      label: s.tabs.percentage,
      value: 'percentage',
      icon: <SplitPercentageIcon className="text-accent" />,
    },
  ];

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
        {/* Tabs */}
        <SegmentedControl
          variant="raised"
          aria-label="Split mode"
          options={splitOptions}
          value={mode}
          onValueChange={(val) => setMode(val as SplitMode)}
          className="w-fit justify-stretch bg-[#DCD6F2] [&>button]:px-4 [&>button]:py-0 [&>button]:text-xs [&>button]:font-medium **:text-accent"
          buttonClassName="min-h-12.5"
        />

        {/* Clear all / Select all · Paid by. The link toggles: with everyone checked it CLEARS all
            (unchecks + resets the typed values); with anyone unchecked it SELECTS all (re-checks the
            members and rebalances the auto rows so the shares still reconcile). */}
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => {
              if (allChecked) {
                setChecked(Object.fromEntries(scopedMembers.map((m) => [m.id, false])));
                setPctManual({});
                setPct(Object.fromEntries(scopedMembers.map((m) => [m.id, '0.00'])));
                setAmtManual({});
                setAmt(Object.fromEntries(scopedMembers.map((m) => [m.id, '0.00'])));
              } else {
                const nextChecked = Object.fromEntries(scopedMembers.map((m) => [m.id, true]));
                setChecked(nextChecked);
                // `balancePct` / `balanceAmt` keep the 0.00 defaults while NOTHING is hand-typed —
                // which is exactly the state "Clear all" leaves, so Select all used to re-check
                // everyone at 0.00 (the split never added up). With no manual rows, split the
                // full 100% / bill evenly across everyone just selected; with manual rows, keep
                // them and rebalance the auto rows as before.
                const allIds = scopedMembers.map((m) => m.id);
                const anyPctManual = memberIds.some((id) => pctManual[id]);
                setPct((prev) =>
                  anyPctManual
                    ? balancePct(memberIds, nextChecked, pctManual, prev)
                    : { ...prev, ...distributePctEven(allIds, 100) },
                );
                const anyAmtManual = memberIds.some((id) => amtManual[id]);
                setAmt((prev) =>
                  anyAmtManual
                    ? balanceAmt(memberIds, nextChecked, amtManual, prev, totalMinor)
                    : { ...prev, ...distributeAmtEven(allIds, totalMinor) },
                );
              }
            }}
            className="cursor-pointer font-semibold text-fg underline hover:opacity-80"
          >
            {allChecked ? s.clearAll : s.selectAll}
          </button>
          <span className="italic text-fg">{`${s.paidBy} ${displayPayerName}`}</span>
        </div>

        {/* Members */}
        <ul className="max-h-100 overflow-y-auto">
          {scopedMembers.map((member) => {
            const isChecked = Boolean(checked[member.id]);
            return (
              <li key={member.id} className="border-b border-divider last:border-b-0">
                <div
                  role="checkbox"
                  tabIndex={0}
                  aria-checked={isChecked}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).tagName === 'INPUT') return;
                    toggleChecked(member.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      if ((e.target as HTMLElement).tagName === 'INPUT') return;
                      e.preventDefault();
                      toggleChecked(member.id);
                    }
                  }}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left select-none outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg cursor-pointer hover:bg-surface transition-colors"
                >
                  <MemberCheck checked={isChecked} />
                  {/* member.name already falls back to the phone when no real name exists (see
                   *  NewBillModal's splitMembers mapping) — MemberAvatar needs '' (not the already-
                   *  resolved phone-as-name) to tell that case apart from a real name and show its
                   *  silhouette fallback instead of reading the phone's leading "+" as an initial. */}
                  <MemberAvatar
                    name={member.name === member.phone ? '' : member.name}
                    phone={member.phone}
                    avatarUrl={member.avatarUrl}
                    size="md"
                    className="shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-sm', member.isYou ? 'text-info' : 'text-fg')}>
                      {member.name}
                    </p>
                    {(() => {
                      const contact =
                        member.phone && member.phone !== member.name ? member.phone : member.email;
                      if (mode === 'evenly' || mode === 'amount') {
                        // Evenly and Amount modes already display the currency value on the right
                        // (static formatted text or editable currency input), so the secondary line
                        // shows their contact info (phone or email fallback).
                        return contact ? (
                          <p className="truncate text-sm text-fg">{contact}</p>
                        ) : null;
                      }
                      // Shares / percentage: RowControl's slot on the right is the unitless or % input,
                      // so the live computed money value is shown here as subtitle.
                      return (
                        <p className="truncate text-sm text-fg">
                          {currencySymbol(currency)}
                          {fromMinorUnitFormatted(amounts[member.id] ?? 0)}
                        </p>
                      );
                    })()}
                  </div>
                  {/* The editable tabs hide their input for an unchecked member (the money value
                      shows on the secondary line instead). Evenly has no input — its slot IS the
                      amount — so an unchecked member keeps the slot and reads 0.00 (mobile parity)
                      rather than going blank after "Clear all" / an uncheck. */}
                  {isChecked || mode === 'evenly' ? (
                    <RowControl
                      mode={mode}
                      currency={currency}
                      amount={amounts[member.id] ?? 0}
                      shareValue={shares[member.id] ?? ''}
                      pctValue={pct[member.id] ?? ''}
                      amtValue={amt[member.id] ?? ''}
                      ariaName={member.name}
                      onShare={(v) => setShares((p) => ({ ...p, [member.id]: v }))}
                      onPct={(v) => handlePctChange(member.id, v)}
                      onPctBlur={() => handlePctBlur(member.id)}
                      onAmt={(v) => handleAmtChange(member.id, v)}
                      onAmtBlur={() => handleAmtBlur(member.id)}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-center gap-2.5 border-t border-border-soft bg-surface-raised px-5 py-3">
        <Button variant="primary" size="md" onClick={handleSave}>
          {t.newBill.save}
        </Button>
        <Button variant="secondary" size="md" onClick={onClose}>
          {t.newBill.cancel}
        </Button>
      </div>
    </>
  );
}

/**
 * "Split Expenses" modal (Figma) — split the bill across group members by even / shares /
 * percentage / amount, with live per-member figures. Opened from the New Bill "Split" field.
 */
export function SplitModal({
  open,
  onOpenChange,
  members,
  totalMinor,
  currency,
  payerName,
  initial,
  splitMode,
  onSave,
}: SplitModalProps) {
  const s = t.newBill.splitModal;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-60 flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-169 -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface-raised shadow-overlay focus:outline-none"
        >
          <div className="flex items-center gap-4 border-b border-border bg-header-bar px-5 py-2">
            <Dialog.Title className="flex-1 text-lg font-semibold text-fg-strong">
              {s.title} {currencySymbol(currency)} {fromMinorUnitFormatted(totalMinor)}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t.newBill.close}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>
          <SplitForm
            members={members}
            totalMinor={totalMinor}
            currency={currency}
            payerName={payerName}
            initial={initial}
            {...(splitMode ? { splitMode } : {})}
            onClose={() => onOpenChange(false)}
            onSave={onSave}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
